/**
 * @fileoverview Formulário de Premissas de Margens
 * 
 * @module MarginAssumptionsForm
 * @description
 * Permite editar premissas de margens (Bruta, EBITDA, Líquida) para ambas empresas.
 * Inclui validação, comparação com histórico e alertas de desvios.
 * 
 * @features
 * - Margens editáveis por empresa e por ano (2025-2029)
 * - Comparação automática com médias históricas
 * - Validação de ranges realistas
 * - Alertas para margens fora do padrão
 * - Progressão visual de melhora/piora
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Alert,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  Divider,
  Chip,
} from '@mui/material';
import { IconTrendingUp, IconTrendingDown, IconDashboard } from '../../utils/icons';
import { Info } from 'lucide-react';

import { PercentageInput } from './CommonInputs';
import { validateMargin } from '../../hooks/useValuationValidations';
import { GlossarioTooltip } from '../glossario/GlossarioTooltip';
import { useGlossarioModal } from '../../hooks/useGlossarioModal';

/**
 * Interface para premissas de margens de uma empresa
 */
export interface MarginAssumptionsData {
  // Margem Bruta (%)
  margemBruta: {
    [ano: number]: number; // 2025-2029
  };
  
  // Margem EBITDA (%)
  margemEbitda: {
    [ano: number]: number; // 2025-2029
  };
  
  // Margem Líquida (%)
  margemLiquida: {
    [ano: number]: number; // 2025-2029
  };
}

/**
 * Props do componente
 */
interface MarginAssumptionsFormProps {
  setima: MarginAssumptionsData;
  studio555: MarginAssumptionsData;
  onSetimaChange: (margens: MarginAssumptionsData) => void;
  onStudio555Change: (margens: MarginAssumptionsData) => void;
  firstProjectionYear?: number; // 🆕 Ano inicial das projeções (default: 2025)
}

/**
 * Defaults baseados no modelo Excel e histórico
 */
const DEFAULTS_SETIMA: MarginAssumptionsData = {
  margemBruta: {
    2025: 0.62, // 62% - tendência de melhora
    2026: 0.63,
    2027: 0.64,
    2028: 0.65,
    2029: 0.65,
  },
  margemEbitda: {
    2025: 0.1784, // 17,84% - progressão para target 18%
    2026: 0.1792,
    2027: 0.18,
    2028: 0.18,
    2029: 0.18,
  },
  margemLiquida: {
    2025: 0.095, // 9,5% - melhora gradual
    2026: 0.10,
    2027: 0.105,
    2028: 0.11,
    2029: 0.11,
  },
};

const DEFAULTS_555: MarginAssumptionsData = {
  margemBruta: {
    2025: 0.65, // 65% - modelo escalável
    2026: 0.66,
    2027: 0.67,
    2028: 0.68,
    2029: 0.68,
  },
  margemEbitda: {
    2025: 0.36, // 36% - startup com margens altas
    2026: 0.37,
    2027: 0.38,
    2028: 0.39,
    2029: 0.40,
  },
  margemLiquida: {
    2025: 0.25, // 25% - rentabilidade elevada
    2026: 0.26,
    2027: 0.27,
    2028: 0.28,
    2029: 0.28,
  },
};

/**
 * Médias históricas (referência)
 */
const HISTORICO_SETIMA = {
  margemBruta: 0.60,
  margemEbitda: 0.145,
  margemLiquida: 0.08,
};

const HISTORICO_555 = {
  margemBruta: 0.65,
  margemEbitda: 0.36,
  margemLiquida: 0.25,
};

/**
 * Limites de validação (min/max razoáveis)
 */
const LIMITS = {
  margemBruta: { min: 30, max: 90 }, // 30-90% (já em percentual para PercentageInput)
  margemEbitda: { min: 5, max: 60 }, // 5-60%
  margemLiquida: { min: 1, max: 50 }, // 1-50%
};

/**
 * Componente Principal
 */
export const MarginAssumptionsForm: React.FC<MarginAssumptionsFormProps> = ({
  setima,
  studio555,
  onSetimaChange,
  onStudio555Change,
  firstProjectionYear = 2025, // 🆕 Default para backward compatibility
}) => {
  const [empresaSelecionada, setEmpresaSelecionada] = useState<'setima' | 'studio555'>('setima');
  const { abrir: abrirGlossario } = useGlossarioModal();
  
  // Validações de margens (armazena validações por tipo e ano)
  const [marginValidations, setMarginValidations] = useState<Record<string, ReturnType<typeof validateMargin>>>({});
  
  const margensAtuais = empresaSelecionada === 'setima' ? setima : studio555;
  const onChange = empresaSelecionada === 'setima' ? onSetimaChange : onStudio555Change;
  const historico = empresaSelecionada === 'setima' ? HISTORICO_SETIMA : HISTORICO_555;
  const defaults = empresaSelecionada === 'setima' ? DEFAULTS_SETIMA : DEFAULTS_555;

  // 🆕 Calcular anos dinâmicos baseado em firstProjectionYear
  const anos = [
    firstProjectionYear,
    firstProjectionYear + 1,
    firstProjectionYear + 2,
    firstProjectionYear + 3,
    firstProjectionYear + 4,
  ];

  // Detectar alertas (margens fora do esperado)
  const alertas: string[] = [];
  
  // Verificar se EBITDA > Bruta (impossível)
  anos.forEach(ano => {
    const brutaValue = margensAtuais.margemBruta[ano] || 0;
    const ebitdaValue = margensAtuais.margemEbitda[ano] || 0;
    const liquidaValue = margensAtuais.margemLiquida[ano] || 0;
    
    if (ebitdaValue > brutaValue) {
      alertas.push(`Margem EBITDA ${ano} não pode ser maior que Margem Bruta`);
    }
    if (liquidaValue > ebitdaValue) {
      alertas.push(`Margem Líquida ${ano} não pode ser maior que Margem EBITDA`);
    }
  });

  // Verificar desvios significativos do histórico
  const firstYearEbitda = margensAtuais.margemEbitda[firstProjectionYear] || 0;
  const desvioEbitda = Math.abs(firstYearEbitda - historico.margemEbitda);
  if (desvioEbitda > 0.10) {
    alertas.push(`Margem EBITDA ${firstProjectionYear} muito diferente do histórico (${(historico.margemEbitda * 100).toFixed(1)}%)`);
  }

  const handleMargemChange = (tipo: 'margemBruta' | 'margemEbitda' | 'margemLiquida', ano: number, valor: number) => {
    // Validar margem
    const tipoLabel = tipo === 'margemBruta' ? 'Bruta' : tipo === 'margemEbitda' ? 'EBITDA' : 'Líquida';
    const validation = validateMargin(valor, tipoLabel, ano);
    
    // Armazenar validação com chave única
    const validationKey = `${tipo}-${ano}`;
    setMarginValidations(prev => ({
      ...prev,
      [validationKey]: validation,
    }));
    
    // Aplicar mudança
    onChange({
      ...margensAtuais,
      [tipo]: {
        ...margensAtuais[tipo],
        [ano]: valor,
      },
    });
  };

  const handleReset = () => {
    onChange(defaults);
  };

  // Calcular tendência (crescente/decrescente) - ✅ ANOS DINÂMICOS
  const calcularTendencia = (tipo: 'margemBruta' | 'margemEbitda' | 'margemLiquida') => {
    const primeiroAno = firstProjectionYear;
    const ultimoAno = firstProjectionYear + 4;
    const margemInicial = margensAtuais[tipo][primeiroAno] || 0;
    const margemFinal = margensAtuais[tipo][ultimoAno] || 0;
    const diff = margemFinal - margemInicial;
    
    if (Math.abs(diff) < 0.01) return { label: 'Estável', color: 'default' as const };
    if (diff > 0) return { label: `+${(diff * 100).toFixed(1)}pp`, icon: <IconTrendingUp size={16} />, color: 'success' as const };
    return { label: `${(diff * 100).toFixed(1)}pp`, icon: <IconTrendingDown size={16} />, color: 'error' as const };
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconDashboard size={24} />
          Premissas de Margens ({firstProjectionYear}-{firstProjectionYear + 4})
        </Typography>
        
        <ToggleButtonGroup
          value={empresaSelecionada}
          exclusive
          onChange={(_, newValue) => newValue && setEmpresaSelecionada(newValue)}
          size="small"
        >
          <ToggleButton value="setima">Sétima</ToggleButton>
          <ToggleButton value="studio555">555 Studios</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {alertas.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight="bold">Alertas de Validação:</Typography>
          <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
            {alertas.map((alerta, idx) => (
              <li key={idx}><Typography variant="body2">{alerta}</Typography></li>
            ))}
          </ul>
        </Alert>
      )}

      <Alert severity="info" icon={<Info size={20} />} sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Histórico {empresaSelecionada === 'setima' ? 'Sétima' : '555 Studios'}:</strong>{' '}
          Margem Bruta {(historico.margemBruta * 100).toFixed(1)}% • 
          EBITDA {(historico.margemEbitda * 100).toFixed(1)}% • 
          Líquida {(historico.margemLiquida * 100).toFixed(1)}%
        </Typography>
      </Alert>

      {/* Margem Bruta */}
      <Box mb={3}>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            Margem Bruta
            <GlossarioTooltip
              termo="NOPAT"
              definicao="Margem Bruta = (Receita - CMV) / Receita. Mede quanto sobra após custos diretos."
              onClick={() => abrirGlossario('NOPAT')}
            />
          </Typography>
          <Chip 
            {...calcularTendencia('margemBruta')} 
            size="small" 
          />
        </Box>
        
        <Grid container spacing={2}>
          {anos.map(ano => {
            const validationKey = `margemBruta-${ano}`;
            const validation = marginValidations[validationKey];
            
            return (
              <Grid item xs={6} sm={6} md={2.4} key={ano}>
                <PercentageInput
                  label={ano.toString()}
                  value={margensAtuais.margemBruta[ano] || 0}
                  onChange={(valor) => handleMargemChange('margemBruta', ano, valor)}
                  min={LIMITS.margemBruta.min}
                  max={LIMITS.margemBruta.max}
                  helpText={`Lucro Bruto / Receita Líquida`}
                />
                {/* Validação de Margem Bruta */}
                {validation && !validation.isValid && validation.error && (
                  <Alert severity="error" sx={{ mt: 1, fontSize: '0.75rem' }}>
                    {validation.error}
                  </Alert>
                )}
                {validation && validation.isValid && validation.warning && (
                  <Alert severity="warning" sx={{ mt: 1, fontSize: '0.75rem' }}>
                    {validation.warning}
                  </Alert>
                )}
              </Grid>
            );
          })}
        </Grid>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Margem EBITDA */}
      <Box mb={3}>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            Margem EBITDA
            <GlossarioTooltip
              termo="NOPAT"
              definicao="Margem EBITDA = EBITDA / Receita. Mede rentabilidade operacional antes de juros, impostos e depreciação."
              onClick={() => abrirGlossario('NOPAT')}
            />
          </Typography>
          <Chip 
            {...calcularTendencia('margemEbitda')} 
            size="small" 
          />
        </Box>
        
        <Grid container spacing={2}>
          {anos.map(ano => {
            const validationKey = `margemEbitda-${ano}`;
            const validation = marginValidations[validationKey];
            
            return (
              <Grid item xs={6} sm={6} md={2.4} key={ano}>
                <PercentageInput
                  label={ano.toString()}
                  value={margensAtuais.margemEbitda[ano] || 0}
                  onChange={(valor) => handleMargemChange('margemEbitda', ano, valor)}
                  min={LIMITS.margemEbitda.min}
                  max={LIMITS.margemEbitda.max}
                  helpText={`EBITDA / Receita Líquida`}
                />
                {/* Validação de Margem EBITDA */}
                {validation && !validation.isValid && validation.error && (
                  <Alert severity="error" sx={{ mt: 1, fontSize: '0.75rem' }}>
                    {validation.error}
                  </Alert>
                )}
                {validation && validation.isValid && validation.warning && (
                  <Alert severity="warning" sx={{ mt: 1, fontSize: '0.75rem' }}>
                    {validation.warning}
                  </Alert>
                )}
              </Grid>
            );
          })}
        </Grid>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Margem Líquida */}
      <Box mb={3}>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            Margem Líquida
            <GlossarioTooltip
              termo="NOPAT"
              definicao="Margem Líquida = Lucro Líquido / Receita. Mede lucro final após todas as despesas e impostos."
              onClick={() => abrirGlossario('NOPAT')}
            />
          </Typography>
          <Chip 
            {...calcularTendencia('margemLiquida')} 
            size="small" 
          />
        </Box>
        
        <Grid container spacing={2}>
          {anos.map(ano => {
            const validationKey = `margemLiquida-${ano}`;
            const validation = marginValidations[validationKey];
            
            return (
              <Grid item xs={6} sm={6} md={2.4} key={ano}>
                <PercentageInput
                  label={ano.toString()}
                  value={margensAtuais.margemLiquida[ano] || 0}
                  onChange={(valor) => handleMargemChange('margemLiquida', ano, valor)}
                  min={LIMITS.margemLiquida.min}
                  max={LIMITS.margemLiquida.max}
                  helpText={`Lucro Líquido / Receita Líquida`}
                />
                {/* Validação de Margem Líquida */}
                {validation && !validation.isValid && validation.error && (
                  <Alert severity="error" sx={{ mt: 1, fontSize: '0.75rem' }}>
                    {validation.error}
                  </Alert>
                )}
                {validation && validation.isValid && validation.warning && (
                  <Alert severity="warning" sx={{ mt: 1, fontSize: '0.75rem' }}>
                    {validation.warning}
                  </Alert>
                )}
              </Grid>
            );
          })}
        </Grid>
      </Box>

      {/* Box Informativo */}
      <Alert severity="success" sx={{ mt: 3 }} icon={<Info size={20} />}>
        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Info size={16} />
          <strong>Dica:</strong> Margens devem manter hierarquia: Bruta {'>'} EBITDA {'>'} Líquida. 
          {empresaSelecionada === 'setima' 
            ? ' Sétima: foco em eficiência operacional gradual.'
            : ' 555 Studios: modelo escalável com margens naturalmente mais altas.'}
        </Typography>
      </Alert>

      <Box display="flex" gap={2} mt={3}>
        <Button variant="outlined" onClick={handleReset}>
          Restaurar Padrões
        </Button>
      </Box>
    </Paper>
  );
};

export default MarginAssumptionsForm;
